import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Heart, type LucideIconData } from 'lucide-angular';

import { PROFILE_COPY } from '../../../features/profile/profile.copy';
import { AuthModalService } from '../../services/auth-modal.service';
import { AuthService } from '../../services/auth.service';
import { WishlistStore } from '../../services/wishlist.store';
import { IconComponent } from '../icon/icon.component';

/**
 * Id de producto válido, o `null`: de 1 a 15 dígitos decimales (por debajo del `[0-9]{1,18}` de la API, siempre entero
 * seguro) y mayor que 0. La misma regla para `number` y `string` (AS-W2-3): `String(1.5)`, `String(1e21)` o un número de
 * 16 dígitos no casan.
 */
function productIdOf(raw: number | string): number | null {
  const text = String(raw);
  if (!/^\d{1,15}$/.test(text)) return null;
  const id = Number(text);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * Corazón de la lista de deseos (W2 de B10, B29): **autocontenido**, lee y escribe `WishlistStore` y no tiene salidas;
 * los anfitriones (tarjeta, quickview, ficha) solo lo colocan. Relleno por CSS cuando el producto está guardado.
 *
 * El clic se trata en el host: `stopPropagation` + `preventDefault` siempre (dentro de la tarjeta, el clic no navega a
 * la ficha), también con el botón deshabilitado por una petición en vuelo. Invitado → modal de login, sin toast y sin
 * reanudar la acción tras entrar (vuelve a pulsar). Con un id que la API no admite no se pinta.
 *
 * El tamaño lo pone el anfitrión con `--wishlist-btn-size` (por defecto 2.25rem) e `iconSize`.
 */
@Component({
  selector: 'app-wishlist-button',
  imports: [IconComponent],
  template: `
    @if (id() !== null) {
      <button
        type="button"
        class="wishlist-btn"
        [class.wishlist-btn--saved]="saved()"
        [attr.aria-pressed]="saved()"
        [attr.aria-label]="label()"
        [attr.title]="label()"
        [disabled]="pending()"
      >
        <app-icon [icon]="heartIcon" [size]="iconSize()" color="currentColor" />
      </button>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .wishlist-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--wishlist-btn-size, 2.25rem);
      height: var(--wishlist-btn-size, 2.25rem);
      padding: 0;
      border: 1px solid var(--color-border-primary);
      border-radius: var(--radius-button);
      background-color: var(--color-surface-primary);
      color: var(--color-text-secondary);
      box-shadow: var(--shadow-button);
      cursor: pointer;
      transition:
        color var(--transition-fast),
        border-color var(--transition-fast);
    }

    .wishlist-btn:hover:not([disabled]) {
      color: var(--color-action-primary);
      border-color: var(--color-action-primary);
    }

    .wishlist-btn:focus-visible {
      outline: 2px solid var(--color-brand-secondary);
      outline-offset: 2px;
    }

    .wishlist-btn--saved {
      color: var(--color-action-primary);
    }

    /* El SVG de lucide lo pinta otro componente: sin atributo de encapsulación, se alcanza con ::ng-deep. */
    .wishlist-btn--saved ::ng-deep svg {
      fill: currentColor;
    }

    .wishlist-btn[disabled] {
      cursor: progress;
      opacity: 0.7;
    }

    @media (prefers-reduced-motion: reduce) {
      .wishlist-btn {
        transition: none;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(click)': 'onClick($event)' },
})
export class WishlistButtonComponent {
  private readonly store = inject(WishlistStore);
  private readonly auth = inject(AuthService);
  private readonly authModal = inject(AuthModalService);
  private readonly copy = PROFILE_COPY.WISHLIST;

  readonly productId = input.required<number | string>();
  readonly iconSize = input(16);

  protected readonly heartIcon: LucideIconData = Heart;
  protected readonly id = computed(() => productIdOf(this.productId()));
  protected readonly saved = computed(() => {
    const id = this.id();
    return id !== null && this.store.isSaved(id);
  });
  protected readonly pending = computed(() => {
    const id = this.id();
    return id !== null && this.store.pending(id);
  });
  protected readonly label = computed(() => (this.saved() ? this.copy.ARIA_REMOVE : this.copy.ARIA_SAVE));

  protected onClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const id = this.id();
    if (id === null || this.pending()) return;
    if (!this.auth.isAuthenticated()) {
      this.authModal.open('login');
      return;
    }
    this.store.toggle(id);
  }
}
